import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Sparkles, TrendingUp, Eye, X, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WINDOW_OPTIONS = [7, 30, 90];

/**
 * Admin Tour Funnel — shows the Portfolio-Aware Advisor onboarding
 * funnel (shown → dismissed → first message sent). Hidden entirely for
 * Free users (the backend endpoint returns 403 and we render nothing).
 *
 * Metrics are all UNIQUE users per stage. Backed by
 * GET /api/admin/advisor/tour-funnel?days=N.
 */
export default function AdvisorTourFunnel() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${BACKEND_URL}/api/admin/advisor/tour-funnel`, {
        params: { days },
        withCredentials: true,
      })
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setGated(false);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 403) setGated(true);
        setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  if (gated) return null;

  return (
    <section
      data-testid="advisor-tour-funnel-card"
      className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824] p-5 shadow-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
          </div>
          <div>
            <h3 className="font-primary font-bold text-base text-slate-900 dark:text-slate-100">
              {t('adminAdvisor.tour.title')}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {t('adminAdvisor.tour.subtitle')}
            </p>
          </div>
        </div>
        <div className="flex gap-1 rounded-full bg-slate-100 dark:bg-slate-800 p-0.5">
          {WINDOW_OPTIONS.map((d) => (
            <button
              key={d}
              data-testid={`advisor-tour-funnel-window-${d}`}
              onClick={() => setDays(d)}
              className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${
                days === d
                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
            >
              {t('adminAdvisor.tour.windowLabel', { days: d })}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="text-xs text-slate-400" data-testid="advisor-tour-funnel-loading">
          {t('common.loading')}
        </div>
      )}
      {!loading && data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Stage
            icon={Eye}
            label={t('adminAdvisor.tour.stages.shown')}
            value={data.shown}
            testid="tour-stage-shown"
          />
          <Stage
            icon={X}
            label={t('adminAdvisor.tour.stages.dismissed')}
            value={data.dismissed}
            rate={data.dismiss_rate}
            testid="tour-stage-dismissed"
          />
          <Stage
            icon={Send}
            label={t('adminAdvisor.tour.stages.activated')}
            value={data.activated}
            rate={data.overall_activation_rate}
            highlight
            testid="tour-stage-activated"
          />
        </div>
      )}
      {!loading && data && data.shown > 0 && (
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-3 inline-flex items-center gap-1.5">
          <TrendingUp className="w-3 h-3" aria-hidden="true" />
          {t('adminAdvisor.tour.summary', {
            activationRate: Math.round((data.overall_activation_rate || 0) * 100),
          })}
        </p>
      )}
    </section>
  );
}

function Stage({ icon: Icon, label, value, rate, highlight, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border p-3 flex flex-col gap-1 ${
        highlight
          ? 'border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/60 dark:bg-emerald-950/20'
          : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40'
      }`}
    >
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">
        <Icon className="w-3 h-3" aria-hidden="true" />
        {label}
      </span>
      <span className={`font-primary font-extrabold text-2xl tabular-nums ${
        highlight ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-900 dark:text-slate-100'
      }`}>
        {value}
      </span>
      {rate != null && (
        <span className="text-[11px] text-slate-500 dark:text-slate-400 tabular-nums">
          {Math.round(rate * 100)}%
        </span>
      )}
    </div>
  );
}
